// src/app/api/contactApi.ts
import { baseApi } from "./baseApi";

export type ContactType =
  | "all"
  | "lead"
  | "custom_funnel"
  | "faith_that_works"
  | "monetize_knowledge"
  | "wealth_method";

export type ContactStatusMap = Record<string, string>;

export type ContactEvent = {
  type: string;
  status?: string;
  eventCity?: string;
  venue?: string;
  eventDate?: string | Date | null;
  id?: string;
};

export type ContactPurchase = {
  type: string;
  status?: string;
  product?: any;
  orderId?: string | null;
  paystackReference?: string;
  id?: string;
};

export type Contact = {
  id: string; // email preferred
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;

  types: string[]; // which sources/models they appear in
  statuses: ContactStatusMap;

  funnels: string[];
  sources: string[];
  tags: string[];

  metrics: {
    emailOpens?: number;
    emailClicks?: number;
    downloads?: number;
    purchases?: number;
    webinarsEnrolled?: number;
    funnelStepsCompleted?: number;
    lastActivityAt?: string | Date | null;
  };

  consent?: {
    email?: boolean;
    sms?: boolean;
  };

  meta?: {
    webinarAccessIds?: string[];
  };

  events: ContactEvent[];
  purchases: ContactPurchase[];

  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;

  // optional raw aggregation
  _records?: Array<{
    type: string;
    id: string;
    status?: string | null;
    source?: string | null;
    funnelName?: string | null;
    createdAt?: string | Date | null;
    updatedAt?: string | Date | null;
  }>;
};

export type ContactsResponse = {
  success: boolean;
  message: string;
  data: {
    contacts: Contact[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
};

export const contactApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContacts: builder.query<
      ContactsResponse,
      {
        type?: ContactType;
        q?: string;
        email?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: "/contacts",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data?.contacts
          ? [
              { type: "Contacts" as const, id: "LIST" },
              ...result.data.contacts.map((c) => ({
                type: "Contacts" as const,
                id: c.id,
              })),
            ]
          : [{ type: "Contacts" as const, id: "LIST" }],
    }),

    getContactByEmail: builder.query<
      ContactsResponse,
      { email: string; type?: ContactType }
    >({
      query: ({ email, type = "all" }) => ({
        url: "/contacts",
        method: "GET",
        params: { email, type, page: 1, limit: 50 },
      }),
      providesTags: (result) =>
        result?.data?.contacts?.[0]
          ? [{ type: "Contacts" as const, id: result.data.contacts[0].id }]
          : [{ type: "Contacts" as const, id: "UNKNOWN" }],
    }),
  }),
});

export const { useGetContactsQuery, useGetContactByEmailQuery } = contactApi;
